import { useMemo } from 'react';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { Box, Stack } from '@mui/material';
import { AvailabilityChart } from './AvailabilityChart';
import { DbMonitoringChartConfiguration } from '../DbMonitoringChartConfiguration';
import _debounce from 'lodash/debounce';
import { ApexOptions } from 'apexcharts';
import { ResponsiveVirtualizedChartGrid } from './ResponsiveVirtualizedChartGrid';
import { monitoringCustomStyles } from '../monitoringCustomStyles';
import { useMonitoring } from './useMonitoring';
import { FullScreenChart } from '../../../../common/charts/FullScreenChart';

export default function DbMonitoring({ database }) {
  const {
    scrollRef,
    combinedAdaptedResponse,
    startTime,
    endTime,
    searchText,
    updateSearchText,
    metricOption,
    setMetricOption,
    updateStartEndTime,
    fetchAllMetrics,
    range,
    setRange,
    instance,
    setInstance,
    granularity,
    setGranularity,
    xMin,
    xMax,
    updateTimeUsingChartZoomIn,
    availability,
    getLatestXRange,
    dbMetrics,
    getFilteredCharts,
    combinedIsLoading,
    shouldRenderCharts,
    fullScreenChart,
    setFullScreenChart,
  } = useMonitoring({ database });
  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        toolbar: {
          offsetX: -20,
        },
        animations: {
          enabled: false,
          easing: 'easeinout',
          speed: 0,
          animateGradually: {
            enabled: false,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 300,
          },
        },

        events: {
          zoomed: (chart: any, options?: any) => {
            updateTimeUsingChartZoomIn(chart, options);
          },
        },
      },
      xaxis: {
        min: getLatestXRange()?.xMin,
        max: getLatestXRange()?.xMax,
      },
    }),
    [updateTimeUsingChartZoomIn, getLatestXRange],
  );

  const Charts = useMemo(() => {
    return (
      <ResponsiveVirtualizedChartGrid
        monitoringMetrics={dbMetrics}
        filteredMonitoringMetrics={getFilteredCharts}
        scrollRef={scrollRef}
        chartOptions={chartOptions}
        isLoadingCharts={combinedIsLoading}
        granularityControl={{ granularity, setGranularity }}
        startTime={startTime}
        endTime={endTime}
        fullScreenChart={fullScreenChart}
        setFullScreenChart={setFullScreenChart}
      />
    );
  }, [
    dbMetrics,
    shouldRenderCharts,
    combinedIsLoading,
    chartOptions,
    granularity,
    setGranularity,
    startTime,
    endTime,
    fullScreenChart,
    setFullScreenChart,
    getFilteredCharts,
    scrollRef,
  ]);

  return (
    <Box p={1} height={'500px'} sx={{ overflow: 'hidden' }}>
      <Stack
        sx={monitoringCustomStyles.DbMonitoring.mainComponent}
        ref={scrollRef}
      >
        <Stack sx={monitoringCustomStyles.DbMonitoring.configuration}>
          <DbMonitoringChartConfiguration
            database={database}
            metricsRes={combinedAdaptedResponse}
            startTime={startTime}
            endTime={endTime}
            searchText={searchText}
            setSearchText={updateSearchText}
            metricOption={metricOption}
            setMetricOption={setMetricOption}
            updateStartEndTime={updateStartEndTime}
            fetchMetrics={fetchAllMetrics}
            range={range}
            setRange={setRange}
            instance={instance}
            setInstance={setInstance}
            granularity={granularity}
            setGranularity={setGranularity}
          />

          <AvailabilityChart
            xMin={xMin}
            xMax={xMax}
            updateTimeUsingChartZoomIn={updateTimeUsingChartZoomIn}
            availabilityChart={availability}
          />
        </Stack>
        <Stack sx={monitoringCustomStyles.DbMonitoring.charts}>{Charts}</Stack>
      </Stack>
      {fullScreenChart && (
        <FullScreenChart
          fullScreenChart={fullScreenChart}
          setFullScreenChart={setFullScreenChart}
          chartOptions={chartOptions}
          granularityControl={{ granularity, setGranularity }}
          startTime={startTime}
          endTime={endTime}
        />
      )}
    </Box>
  );
}
